<script lang="ts">
  import moment from 'moment';
  import { onDestroy, onMount } from 'svelte';
  import {
    Chart as ChartJS,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
    ArcElement,
    Filler,
  } from 'chart.js';
  import { Chart } from 'svelte-chartjs';
  import zoomPlugin from 'chartjs-plugin-zoom';
  import loader from 'src/components/cabinet/#assets/loader.svg';
  import { getGooglePlayAnalytics } from 'src/components/cabinet/#services/googlePlayAnalytics'
  import Table from '../Table.svelte';
  import { options } from '../../configs/BarOptions';
  import DateRange from '../common/DateRange.svelte';
  import GameSelect from '../common/GameSelect.svelte';
  import { topOptions } from '../../configs/TopOptions';
  import { squezzeBranches } from '../../utils/branches';
  import { requestsParams } from '../../configs/Requests';
  import CountriesSelect from '../common/CountriesSelect.svelte';
  import { resetChart, colors, transparentColors } from '../../utils/common';
  import { getTopByCountries } from '../../../#services/getTopByCountries';

  let chart, topChart;
  let table, topTable;

  let game = 'all';
  let category = 'all';
  let subcategory = 'all';
  let country = null;

  let labels = [];
  let datasets = [];

  let data = { labels, datasets };

  let topLabels = [];
  let topDatasets = [];

  let updatingTopChart = false;
  let topCache;

  let topData = { labels: topLabels, datasets: topDatasets };

  let startDate = 'startDate'
  let endDate = 'endDate'

  const minDate = '2022-05-01';
  const maxDate = moment().format('YYYY-MM-DD');

  ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    ArcElement,
    Filler,
    zoomPlugin,
  );

  function refreshTop() {
    const requestData = {
      countries: [],
      game: game !== 'all' ? game : undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : undefined,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    };

    getTopByCountries.send({ data: requestData });
  }

  function refreshData() {
    let branchesData;

    if (category === 'offers') {
      branchesData = requestsParams.googlePlay['all'][category][subcategory](country?.value, (game !== 'all' ? game : undefined));
    } else {
      branchesData = requestsParams.googlePlay['all'][category](country?.value, (game !== 'all' ? game : undefined));
    }

    const requestData = {
      branches: branchesData,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    };

    getGooglePlayAnalytics.send({ data: requestData });
  }

  function handleUpdate(_) {
    redrawTopChart();
  }

  function refresh() {
    resetChart(chart, labels, datasets);
    resetChart(topChart, topLabels, topDatasets);
    refreshTop();
    refreshData();
  }

  function redrawTopChart() {
    updatingTopChart = true;

    const checkedCountries = topTable['%'].filter((branch) => branch.checked).map((branch) => branch.country);

    let idx = 0;
    for (const branch of topCache) {
      if (!checkedCountries.includes(branch.country)) continue;

      const data = [];

      branch.values.forEach(e => {
        data.push(e.amount);
      });

      topDatasets.push({
        label: branch.country,
        borderColor: colors[idx],
        backgroundColor: transparentColors[idx],
        fill: idx === 0 ? 'start' : '-1',
        data,
      });
      idx++;
    }

    setTimeout(() => {
      updatingTopChart = false;
    }, 500)
  }

  refresh();

  getTopByCountries.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
        topCache = value.value.result;

        value.value.result[0].values.forEach(e => {
          topLabels.push(e.date);
        });

        let idx = 0;
        for (const branch of value.value.result.slice(0, 5)) {
          const data = [];

          branch.values.forEach(e => {
            data.push(e.amount);
          });

          topDatasets.push({
            label: branch.country,
            borderColor: colors[idx],
            backgroundColor: transparentColors[idx],
            fill: idx === 0 ? 'start' : '-1',
            data,
          });
          idx++;
        }

        try {
          const sumAmount = value.value.result.reduce((a, b) => a + b.values.reduce((d, e) => d + e.amount, 0), 0);

          topTable = {
            '%': [...value.value.result].reverse().map((country) => ({
              country: country.country,
              amount: 100 / sumAmount * country.values.reduce((a, b) => a + b.amount, 0),
              checked: false,
            })),
          };

          const length = topTable['%'].length;

          topTable['%'][length - 1].checked = true;
          topTable['%'][length - 2].checked = true;
          topTable['%'][length - 3].checked = true;
          topTable['%'][length - 4].checked = true;
          topTable['%'][length - 5].checked = true;
        } catch (err) {}
    }
  });

  getGooglePlayAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      value.value.branches[0].values.forEach(e => {
        labels.push(e.date);
      });

      let idx = 0;
      for (const branch of value.value.branches) {
        const data = [];

        branch.values.forEach(e => {
          data.push(e.amount);
        });

        datasets.push({
          label: branch.label,
          backgroundColor: colors[idx],
          data,
          stack: 0
        });
        idx++;
      }

      try {
        if (category === 'all') {
            table = {
                'LIS ($)': value.value.branches[0]?.values,
                'Offers ($)': value.value.branches[1]?.values,
                'Memberships ($)': value.value.branches[2]?.values,
                'Total ($)': squezzeBranches([
                    value.value.branches[0]?.values,
                    value.value.branches[1]?.values,
                    value.value.branches[2]?.values,
                ]),
            }
        }

        if (category === 'lis') {
            table = {
                'LIS 1 ($)': value.value.branches[0]?.values,
                'LIS 2 ($)': value.value.branches[1]?.values,
                'LIS 3 ($)': value.value.branches[2]?.values,
                'LIS 4 ($)': value.value.branches[3]?.values,
                'LIS 5 ($)': value.value.branches[4]?.values,
                'LIS 6 ($)': value.value.branches[5]?.values,
                'Total ($)': squezzeBranches(value.value.branches.map(b => b.values)),
            }
        }

        if (category === 'offers') {
          if (subcategory === 'all') {
            table = {
              '2X ($)': value.value.branches[0]?.values,
              'Hangar ($)': value.value.branches[1]?.values,
              'Auto merge ($)': value.value.branches[2]?.values,
              'Last chance ($)': value.value.branches[3]?.values,
              'Offline earning ($)': value.value.branches[4]?.values,
              'Starter pack ($)': value.value.branches[5]?.values,
              'Wall street ($)': value.value.branches[6]?.values,
              'Weekly tournament ($)': value.value.branches[7]?.values,
              'Xmas ($)': value.value.branches[8]?.values,
              'Total ($)': squezzeBranches(value.value.branches.map(b => b.values)),
            }
          } else if (subcategory === 'hangar') {
            table = {
              'Hangar 1 ($)': value.value.branches[0]?.values,
              'Hangar 2 ($)': value.value.branches[1]?.values,
              'Total ($)': squezzeBranches(value.value.branches.map(b => b.values)),
            }
          } else if (subcategory === 'lastChance') {
            table = {
              'Last chance 1 ($)': value.value.branches[0]?.values,
              'Last chance 2 ($)': value.value.branches[1]?.values,
              'Last chance 3 ($)': value.value.branches[2]?.values,
              'Last chance 4 ($)': value.value.branches[3]?.values,
              'Total ($)': squezzeBranches(value.value.branches.map(b => b.values)),
            }
          }
        } else if (category === 'memberships') {
          table = {
            'Silver ($)': value.value.branches[0]?.values,
            'Gold ($)': value.value.branches[1]?.values,
            'Platinum ($)': value.value.branches[2]?.values,
            'Total ($)': squezzeBranches(value.value.branches.map(b => b.values)),
          }
        }
      } catch (e) {}
    }
  });

  onDestroy(() => {
    resetChart(chart);
    getGooglePlayAnalytics.reset();
  });

  onMount(() => {
    refresh();
  });
</script>

<div class="buttons">
  <DateRange bind:startDate={startDate} bind:endDate={endDate} {minDate} {maxDate} refreshCallback={() => refresh()} />
</div>

<hr>

<div class="buttons">
  <div class="pc-line">
    <GameSelect bind:creator={game} refreshCallback={() => refresh()} />
  </div>
  <div class="pc-line">
      <span class="date-label">Category</span>
      <select class="form-select" bind:value={category} on:change={refresh}>
        <option value="all">All</option>
        <option value="lis">LIS purchases</option>
        <option value="offers">Offers</option>
        <option value="memberships">Memberships</option>
      </select>
  </div>
  {#if category === 'offers'}   
      <div class="pc-line">
        <span class="date-label">Subcategory</span>
        <select class="form-select" bind:value={subcategory} on:change={refresh}>
          <option value="all">All</option>
          <option value="hangar">Hangar</option>
          <option value="lastChance">Last chance</option>
        </select>
      </div>
  {/if}
  <div class="pc-line">
    <CountriesSelect bind:country={country} refreshCallback={() => refresh()} />
  </div>
</div>
<hr>
<div class="buttons">
  <div class="pc-line">
    <button class="btn btn-primary btn-filter" on:click={refresh}>Refresh</button>
  </div>
</div>
<div class="top-chart">
  <h4>Top by countries</h4>
  {#if $getTopByCountries.error}
    Something went wrong. Try refreshing the page.
  {:else if $getTopByCountries.loading || updatingTopChart}
    <div class="graphic-loading">
      <img src="{loader}" alt="">
    </div>
  {:else}
    <Chart bind:this={topChart} type="line" width={500} height={270} data={topData} options={topOptions} />
  {/if}
  {#if !$getTopByCountries.loading}
    <Table branches={topTable} checkboxes={true} on:update={handleUpdate} />
  {/if}
</div>
<div class="graphic">
  <h4>Profit</h4>
  {#if $getGooglePlayAnalytics.error}
    Something went wrong. Try refreshing the page.
  {:else if $getGooglePlayAnalytics.loading}
    <div class="graphic-loading">
      <img src="{loader}" alt="">
    </div>
  {:else}
    <Chart bind:this={chart} type="bar" width={500} height={270} {data} {options} />
    <Table branches={table} percentsInTotal={true} />
  {/if}
</div>